import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PERMISSION_SERVICE,
  PermissionServiceBase,
  RestApiService,
  SharedDataStore,
  SKIP_ERROR_SNACKBAR,
} from '@ccfx/shared-lib';
import {
  BehaviorSubject,
  EMPTY,
  forkJoin,
  fromEvent,
  interval,
  merge,
  of,
  Subject,
  throwError,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { HubInventoryUpdate, HubNotification, SignalRHubService } from './signalr-hub.service';
import { UserPermissionStore } from './user-permission.store';

// ── Models ──────────────────────────────────────────────────────────────

export interface PendingApprovalCategory {
  /** Unique key for this category */
  key: string;
  /** Display label */
  label: string;
  /** Font-Awesome icon class */
  icon: string;
  /** Pending count */
  count: number;
  /** Route path to navigate on click */
  route: string;
  /** Children categories (inverted tree) */
  children: PendingApprovalCategory[];
}

// ── Service ─────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class PendingApprovalsService {
  private readonly restApi = inject(RestApiService);
  private readonly permissions = inject<PermissionServiceBase>(PERMISSION_SERVICE);
  private readonly permissionStore = inject(UserPermissionStore);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hubService = inject(SignalRHubService);

  /** Lazily resolved to avoid triggering SharedDataStore HTTP calls before auth */
  private get sharedDataStore() {
    return this.injector.get(SharedDataStore);
  }

  /** Total pending count across all categories */
  readonly totalCount = signal<number>(0);

  /** Category tree for the dropdown */
  readonly categories = signal<PendingApprovalCategory[]>([]);

  /** Loading state */
  readonly loading = signal<boolean>(false);

  /** Manual refresh trigger */
  private readonly refresh$ = new Subject<void>();

  /**
   * Fallback polling interval in ms (5 minutes).
   * Covers approval categories that don't yet have a dedicated hub event
   * (e.g. wholesale-orders, suppliers, restock-orders).
   * Hub events fire instantly for tier approvals and vault inventory changes.
   */
  private readonly FALLBACK_POLL_INTERVAL = 15 * 60 * 1000;

  /** Guard against duplicate subscriptions if startPolling() is called more than once. */
  private isPolling = false;

  /** HTTP context that suppresses the global error snackbar for background polling */
  private readonly silentContext = new HttpContext().set(SKIP_ERROR_SNACKBAR, true);

  /** User inactivity timeout in ms (2 minutes) */
  private readonly INACTIVITY_TIMEOUT = 2 * 60 * 1000;

  /** Tracks whether the user is currently considered active */
  private readonly userActive$ = new BehaviorSubject<boolean>(true);

  /** Handle for the inactivity debounce timer */
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Start real-time pending-approval tracking via SignalR.
   *
   * - Connects to the notification hub (idempotent).
   * - Immediately fetches the current pending counts.
   * - Re-fetches whenever the server pushes a relevant hub event
   *   (`ReceiveNotification`, `InventoryUpdated`).
   * - Re-fetches on hub reconnection to recover missed events.
   * - Falls back to a 5-minute poll for categories that lack hub events
   *   (orders, suppliers, restock).
   * - Still responds to tab-visible and user-reactivation signals.
   *
   * Call once at app bootstrap after the user profile is confirmed.
   */
  startPolling(): void {
    if (this.isPolling) return;
    this.isPolling = true;

    // Ensure the shared vault list is loaded upfront.
    this.sharedDataStore.loadVaults();

    // Wire up user-activity tracking; handles cleanup via DestroyRef.
    this.setupActivityTracking();

    // Start the SignalR hub connection (no-op if already connected).
    this.hubService.connect();

    // ── Triggers ────────────────────────────────────────────────────────────

    // Emit whenever the tab becomes visible.
    const tabVisible$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
    );

    // Emit when the user comes back from being inactive.
    const userReactivated$ = this.userActive$.pipe(
      distinctUntilChanged(),
      filter((active) => active),
      skip(1),
    );

    // Emit (debounced) whenever the server pushes a relevant event.
    // `ReceiveNotification` covers tier-approval changes; `InventoryUpdated`
    // covers vault-adjustment and hold changes.
    const hubUpdates$ = merge(
      this.hubService
        .on<HubNotification>('ReceiveNotification')
        .pipe(
          tap((evt) =>
            console.log('[PendingApprovals] SignalR ReceiveNotification → refresh', evt),
          ),
        ),
      this.hubService
        .on<HubInventoryUpdate>('InventoryUpdated')
        .pipe(
          tap((evt) => console.log('[PendingApprovals] SignalR InventoryUpdated → refresh', evt)),
        ),
    ).pipe(debounceTime(500));

    // Emit when the hub reconnects so we catch any events missed while offline.
    const hubReconnected$ = this.hubService.status$.pipe(
      filter((s) => s === 'connected'),
      skip(1), // skip the initial 'connected' — startWith(undefined) covers first load
      tap(() => console.log('[PendingApprovals] SignalR reconnected → refresh')),
    );

    // ── Main pipeline ────────────────────────────────────────────────────────

    merge(this.refresh$, tabVisible$, userReactivated$, hubUpdates$, hubReconnected$)
      .pipe(
        startWith(undefined),
        // Each trigger restarts the fallback-poll interval so the next
        // scheduled poll is always FALLBACK_POLL_INTERVAL after the last fetch.
        switchMap(() => interval(this.FALLBACK_POLL_INTERVAL).pipe(startWith(0))),
        // Skip ticks while the tab is hidden or the user is idle.
        filter(() => document.visibilityState === 'visible' && this.userActive$.getValue()),
        // On 401 stop entirely (session expired → auth interceptor handles redirect).
        switchMap(() =>
          this.fetchAllPendingCounts().pipe(
            catchError((err) => {
              if (err instanceof HttpErrorResponse && err.status === 401) {
                console.warn('PendingApprovalsService: 401 received — stopping (session expired).');
                return EMPTY;
              }
              return throwError(() => err);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Registers window-level activity listeners and starts the inactivity timer.
   * Listener cleanup is tied to DestroyRef so it is always removed on destroy.
   */
  private setupActivityTracking(): void {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

    const handleActivity = (): void => {
      if (this.inactivityTimer !== null) {
        clearTimeout(this.inactivityTimer);
      }
      // Mark active (no-op if already true — BehaviorSubject dedup via distinctUntilChanged).
      if (!this.userActive$.getValue()) {
        this.userActive$.next(true);
      }
      this.inactivityTimer = setTimeout(() => {
        this.inactivityTimer = null;
        this.userActive$.next(false);
      }, this.INACTIVITY_TIMEOUT);
    };

    events.forEach((ev) => window.addEventListener(ev, handleActivity, { passive: true }));

    this.destroyRef.onDestroy(() => {
      events.forEach((ev) => window.removeEventListener(ev, handleActivity));
      if (this.inactivityTimer !== null) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }
    });

    // Start the initial inactivity countdown.
    handleActivity();
  }

  /** Force an immediate refresh */
  refresh(): void {
    this.refresh$.next();
  }

  // ── Private helpers ─────────────────────────────────────────────────

  /**
   * Fetches pending counts from all approval endpoints in parallel.
   *
   * Current endpoints:
   *  - GET /api/admin/rate-tiers/approvals/pending → TierApprovalRequest[]
   *  - GET /api/admin/vaults → list vaults, then for each:
   *      - GET /api/admin/vaults/{id}/adjustments?status=Pending
   *      - GET /api/admin/vaults/{id}/holds?status=Pending
   *  - GET /api/admin/wholesale-orders → use summary fields for pending cancel/void
   *  - GET /api/admin/suppliers → filter status === 'PendingApproval'
   *
   * NOTE: When the server adds a dedicated "pending-counts" endpoint or
   *       a pub/sub channel, replace these calls accordingly.
   */
  private fetchAllPendingCounts() {
    this.loading.set(true);

    // Permission checks — only fetch data the user is allowed to see.
    // Use action-level permissions so only users who can actually approve
    // each category trigger HTTP calls (prevents 403s for read-only roles).
    const canViewTierApprovals = this.permissionStore.hasActionPermission('rates.approve');
    const canViewVaultApprovals =
      this.permissionStore.hasActionPermission('vault-inventory.approve-adjustment') ||
      this.permissionStore.hasActionPermission('vault-inventory.approve-hold');
    const canViewRestockApprovals =
      this.permissionStore.hasActionPermission('restock.submit-receipt') ||
      this.permissionStore.hasActionPermission('restock.approve-receipt');
    const canViewOrderApprovals =
      this.permissionStore.hasActionPermission('orders.approve-cancellation') ||
      this.permissionStore.hasActionPermission('orders.approve-void');

    const silent = { context: this.silentContext };

    const TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'voided']);

    const tierApprovals$ = canViewTierApprovals
      ? this.restApi
          .get<any[]>('/api/admin/rate-tiers/approvals/pending', silent)
          .pipe(catchError(() => of([])))
      : of([]);

    // Reuse the cached vault list from SharedDataStore — avoids one extra HTTP
    // call per poll cycle.  If the cache is empty (app just started) fall back
    // to a one-time REST call so the feature works on first load.
    const cachedVaults = this.sharedDataStore.vaults();
    const vaults$ = canViewVaultApprovals
      ? cachedVaults.length > 0
        ? of(cachedVaults)
        : this.restApi.get<any[]>('/api/admin/vaults', silent).pipe(catchError(() => of([])))
      : of([]);

    const restockInTransit$ = canViewRestockApprovals
      ? this.restApi
          .get<any[]>('/api/admin/restock-orders?status=InTransit', silent)
          .pipe(catchError(() => of([])))
      : of([]);

    // Fetch all suppliers and filter to those pending approval.
    const supplierPending$ = canViewRestockApprovals
      ? this.restApi.get<any[]>('/api/admin/suppliers', silent).pipe(catchError(() => of([])))
      : of([] as any[]);

    // Use active order summaries directly to identify outstanding cancellation
    // or void approval requests without fanning out detail calls per order.
    const orderPendingApprovals$ = canViewOrderApprovals
      ? this.restApi.get<any[]>('/api/admin/wholesale-orders', silent).pipe(
          switchMap((summaries: any[]) =>
            of(summaries.filter((s: any) => !TERMINAL_STATUSES.has(s.status))),
          ),
          catchError(() => of([])),
        )
      : of([] as any[]);

    return forkJoin({
      tiers: tierApprovals$,
      vaults: vaults$,
      restockInTransit: restockInTransit$,
      supplierPending: supplierPending$,
      orderSummaries: orderPendingApprovals$,
    }).pipe(
      switchMap(({ tiers, vaults, restockInTransit, supplierPending, orderSummaries }) => {
        if (!canViewVaultApprovals || vaults.length === 0) {
          return of({
            tiers,
            adjustments: [] as any[],
            holds: [] as any[],
            restockInTransit,
            supplierPending,
            orderSummaries,
          });
        }

        // Deduplicate by vault ID to prevent issuing duplicate per-vault requests
        // if the API or store returns the same vault more than once.
        const uniqueVaults: any[] = Array.from(new Map(vaults.map((v: any) => [v.id, v])).values());

        // For each unique vault, fetch pending adjustments and active holds
        const adjustmentCalls = uniqueVaults.map((v: any) =>
          this.restApi
            .get<
              { adjustments: any[] } | any[]
            >(`/api/admin/vaults/${v.id}/adjustments?status=Pending`, silent)
            .pipe(
              map((response) =>
                Array.isArray(response) ? response : (response.adjustments ?? []),
              ),
              catchError(() => of([])),
            ),
        );

        const holdCalls = uniqueVaults.map((v: any) =>
          this.restApi
            .get<any[]>(`/api/admin/vaults/${v.id}/holds?status=Pending`, silent)
            .pipe(catchError(() => of([]))),
        );

        return forkJoin({
          adjustments: forkJoin(adjustmentCalls.length ? adjustmentCalls : [of([])]),
          holds: forkJoin(holdCalls.length ? holdCalls : [of([])]),
        }).pipe(
          switchMap(({ adjustments, holds }) =>
            of({
              tiers,
              adjustments: adjustments.flat(),
              holds: holds.flat(),
              restockInTransit,
              supplierPending,
              orderSummaries,
            }),
          ),
        );
      }),
      switchMap((result) => {
        this.buildCategoryTree(
          result.tiers,
          result.adjustments,
          result.holds,
          result.restockInTransit,
          result.supplierPending,
          result.orderSummaries,
        );
        this.loading.set(false);
        return of(result);
      }),
      catchError(() => {
        this.loading.set(false);
        return of(null);
      }),
    );
  }

  private buildCategoryTree(
    tierApprovals: any[],
    adjustments: any[],
    holds: any[],
    restockInTransit: any[],
    supplierPending: any[],
    orderSummaries: any[],
  ): void {
    const categories: PendingApprovalCategory[] = [];

    // ── Rate Tier Approvals ──────────────────────────────────────────
    if (this.permissionStore.hasActionPermission('rates.approve')) {
      categories.push({
        key: 'tier-approvals',
        label: 'Rate Tier Approvals',
        icon: 'fas fa-chart-line',
        count: tierApprovals.length,
        route: '/tier-approvals',
        children: [],
      });
    }

    // ── Vault Inventory Approvals ────────────────────────────────────
    if (
      this.permissionStore.hasActionPermission('vault-inventory.approve-adjustment') ||
      this.permissionStore.hasActionPermission('vault-inventory.approve-hold')
    ) {
      const adjustmentCount = adjustments.length;
      const holdCount = holds.length;
      const vaultTotal = adjustmentCount + holdCount;

      const vaultChildren: PendingApprovalCategory[] = [
        {
          key: 'vault-adjustments',
          label: 'Inventory Adjustments',
          icon: 'fas fa-sliders-h',
          count: adjustmentCount,
          route: '/vault-inventory/adjustments',
          children: [],
        },
        {
          key: 'vault-holds',
          label: 'Inventory Holds',
          icon: 'fas fa-hand-holding',
          count: holdCount,
          route: '/vault-inventory/holds',
          children: [],
        },
      ];

      categories.push({
        key: 'vault-inventory',
        label: 'Vault Inventory',
        icon: 'fas fa-vault',
        count: vaultTotal,
        route: '/vault-inventory',
        children: vaultChildren,
      });
    }

    // ── Restock Order & Supplier Approvals ───────────────────────────
    if (
      this.permissionStore.hasActionPermission('restock.submit-receipt') ||
      this.permissionStore.hasActionPermission('restock.approve-receipt')
    ) {
      const submitReceiptCount = restockInTransit.filter((o: any) => !o.currentReceiptId).length;
      const approveReceiptCount = restockInTransit.filter((o: any) => !!o.currentReceiptId).length;
      const pendingSupplierCount = supplierPending.filter(
        (s: any) => s.status === 'PendingApproval',
      ).length;
      const restockTotal = submitReceiptCount + approveReceiptCount + pendingSupplierCount;

      const restockChildren: PendingApprovalCategory[] = [
        {
          key: 'restock-submit-receipt',
          label: 'Submit Receipt',
          icon: 'fas fa-clipboard',
          count: submitReceiptCount,
          route: '/supplier-restock/restock-orders',
          children: [],
        },
        {
          key: 'restock-approve-receipt',
          label: 'Approve Receipt',
          icon: 'fas fa-clipboard-check',
          count: approveReceiptCount,
          route: '/supplier-restock/restock-orders',
          children: [],
        },
        {
          key: 'supplier-approvals',
          label: 'Supplier Approvals',
          icon: 'fas fa-truck-loading',
          count: pendingSupplierCount,
          route: '/supplier-restock/suppliers',
          children: [],
        },
      ];

      categories.push({
        key: 'restock-orders',
        label: 'Restock Orders',
        icon: 'fas fa-truck',
        count: restockTotal,
        route: '/supplier-restock',
        children: restockChildren,
      });
    }

    // ── Order Management Approvals ───────────────────────────────────
    if (
      this.permissionStore.hasActionPermission('orders.approve-cancellation') ||
      this.permissionStore.hasActionPermission('orders.approve-void')
    ) {
      const cancelCount = orderSummaries.filter(
        (s: any) => s.cancellationReason && !s.cancelledAt,
      ).length;
      const voidCount = orderSummaries.filter((s: any) => s.voidReason && !s.voidedAt).length;
      const orderTotal = cancelCount + voidCount;

      const orderChildren: PendingApprovalCategory[] = [
        {
          key: 'order-cancellations',
          label: 'Pending Cancellations',
          icon: 'fas fa-ban',
          count: cancelCount,
          route: '/order-management/pending-approvals',
          children: [],
        },
        {
          key: 'order-voids',
          label: 'Pending Voids',
          icon: 'fas fa-trash-alt',
          count: voidCount,
          route: '/order-management/pending-approvals',
          children: [],
        },
      ];

      categories.push({
        key: 'order-approvals',
        label: 'Order Approvals',
        icon: 'fas fa-file-alt',
        count: orderTotal,
        route: '/order-management/pending-approvals',
        children: orderChildren,
      });
    }

    this.categories.set(categories);
    this.totalCount.set(categories.reduce((sum, c) => sum + c.count, 0));
  }
}
